import React from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '../../utils';
import { Title, Loader, ReloadIconButton } from '../../elements';
import { AnimalType } from '../../models/animal';
import {
  Tooltip,
  Grid,
  Avatar,
  IconButton,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { LoaderSize } from '../../constants/constants';
import PigletIco from '../../static/piglet01.jpg';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import LocationsService from '../../hooks/LocationsService';
import { LocationAnimal } from '../../models/locations';

interface LocationViewAnimalsProps {
  locationId: string;
  onAnimalsLoaded?: (animals: LocationAnimal[]) => void;
}

const LocationViewAnimals = ({ locationId, onAnimalsLoaded }: LocationViewAnimalsProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const locationService = new LocationsService();

  const [searchResult, setSearchResult] = React.useState([] as LocationAnimal[]);
  const [isBusy, setIsBusy] = React.useState(false);

  // pagination
  const [total, setTotal] = React.useState(0);

  const [shouldCallSearch, setShouldCallSearch] = React.useState(true);
  // * pagination

  const loadAnimals = async () => {
    setIsBusy(true);

    locationService
      .getLocationAnimals(locationId)
      .then((result) => {
        setTotal(result.animals.length);
        setSearchResult(result.animals);
        onAnimalsLoaded?.(result.animals);

        // if (result.animals.length === 0) {
        //   notifications.info(t('f-piglets-empty'));
        // }
      })
      .catch((e) => {
        notifications.error(e?.error?.message ?? e.toString());
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  // go to animal view page (with id param)
  const viewAnimal = (id: string) => {
    navigate(`/animal/${id}`);
  };

  React.useEffect(() => {
    if (shouldCallSearch) {
      loadAnimals();
      setShouldCallSearch(false);
    }
  }, [shouldCallSearch]);

  return (
    <Paper className={classes.paper} elevation={1}>
      <Grid container spacing={1}>
        <Grid container direction="row">
          <Grid container item spacing={1} xs={10}>
            <Grid item>
              <Avatar alt="Animal type" src={PigletIco} />
            </Grid>
            <Grid item>
              <Title>{t('f-piglets')}</Title>
            </Grid>
            <Grid item>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>

          <Grid item xs={2} className={classes.loadPigletsBtnGrid}>
            <ReloadIconButton action={loadAnimals} />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={1}>
            <Table size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>{t('f-com-animal-number')}</TableCell>
                  <TableCell>{t('f-com-date-of-birth')}</TableCell>
                  <TableCell>{t('f-com-animal-type')}</TableCell>
                  <TableCell>{''}</TableCell>
                </TableRow>
              </TableHead>

              {!isBusy && searchResult && searchResult.length > 0 && (
                <TableBody>
                  {searchResult.map((animal) => (
                    <TableRow key={animal.id} hover>
                      <TableCell>{animal.number}</TableCell>
                      <TableCell>{new Date(animal.dateOfBirth).toLocaleDateString()}</TableCell>
                      <TableCell>{AnimalType[animal.animalType]}</TableCell>
                      <TableCell>
                        <Tooltip arrow title={t('f-search-view-animal')}>
                          <IconButton
                            color="primary"
                            aria-label="View animal"
                            size="small"
                            onClick={() => viewAnimal(animal.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </Paper>
          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default LocationViewAnimals;
